import {
  BookOpen,
  ClipboardList,
  Gauge,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  WalletCards,
  X
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { SessionUser } from "../types";

export type NavItem = {
  id: string;
  label: string;
  icon: "dashboard" | "users" | "modules" | "results" | "centers";
};

const icons = {
  dashboard: Gauge,
  users: Users,
  modules: BookOpen,
  results: ClipboardList,
  centers: WalletCards
};

type ShellProps = {
  user: SessionUser;
  items: NavItem[];
  active: string;
  onNavigate: (id: string) => void;
  onLogout: () => void;
  activityLabel?: string;
  activityModuleId?: string;
  children: ReactNode;
};

export function Shell({ user, items, active, onNavigate, onLogout, activityLabel, activityModuleId, children }: ShellProps) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.localStorage.getItem("setup_sidebar_collapsed") === "true";
  });

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value;
      window.localStorage.setItem("setup_sidebar_collapsed", String(next));
      return next;
    });
  };
  const activeItemLabel = activityLabel || items.find((item) => item.id === active)?.label || active;
  useActivityTracker(activeItemLabel, user.id_usuario, user.perfil === "student", activityModuleId);

  return (
    <div className={`app-shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      <aside className={`sidebar ${open ? "sidebar-open" : ""}`}>
        <div className="sidebar-head">
          <div className="brand">
            <div className="brand-mark">GS</div>
            <div className="brand-copy">
              <strong>Grupo Setup</strong>
              <span>Treinamentos</span>
            </div>
          </div>
          <button
            className="sidebar-toggle"
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expandir menu" : "Minimizar menu"}
            title={collapsed ? "Expandir menu" : "Minimizar menu"}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>
        <nav>
          {items.map((item) => {
            const Icon = icons[item.icon];
            return (
              <button
                key={item.id}
                type="button"
                className={active === item.id ? "nav-active" : ""}
                onClick={() => {
                  onNavigate(item.id);
                  setOpen(false);
                }}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={18} />
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <button className="logout-button" type="button" onClick={onLogout} title={collapsed ? "Sair" : undefined}>
          <LogOut size={18} />
          <span className="nav-label">Sair</span>
        </button>
      </aside>

      <div className="shell-main">
        <header className="topbar">
          <button className="icon-button mobile-only" type="button" onClick={() => setOpen((value) => !value)}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div>
            <span>{user.perfil === "admin" ? "Painel administrativo" : "Area do aluno"}</span>
            <strong>{user.nome}</strong>
          </div>
        </header>
        <main>{children}</main>
      </div>
    </div>
  );
}

function useActivityTracker(screen: string, userId: string, enabled: boolean, moduleId?: string) {
  const segmentRef = useRef(createActivitySegment(screen, userId, moduleId));
  const activeStartedAtRef = useRef(Date.now());
  const activeSecondsRef = useRef(0);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (document.visibilityState === "visible") {
      const now = Date.now();
      activeSecondsRef.current += Math.max(0, Math.round((now - activeStartedAtRef.current) / 1000));
      activeStartedAtRef.current = now;
    }
    void flushActivity(segmentRef.current, activeSecondsRef.current);
    segmentRef.current = createActivitySegment(screen, userId, moduleId);
    activeStartedAtRef.current = Date.now();
    activeSecondsRef.current = 0;
  }, [screen, userId, enabled, moduleId]);

  useEffect(() => {
    if (!enabled) return;
    const collectVisibleTime = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      activeSecondsRef.current += Math.max(0, Math.round((now - activeStartedAtRef.current) / 1000));
      activeStartedAtRef.current = now;
    };

    const heartbeat = window.setInterval(() => {
      collectVisibleTime();
      void flushActivity(segmentRef.current, activeSecondsRef.current);
    }, 30000);

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        collectVisibleTime();
        void flushActivity(segmentRef.current, activeSecondsRef.current);
        return;
      }
      activeStartedAtRef.current = Date.now();
    };

    const handleUnload = () => {
      collectVisibleTime();
      void flushActivity(segmentRef.current, activeSecondsRef.current);
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", handleUnload);
      collectVisibleTime();
      void flushActivity(segmentRef.current, activeSecondsRef.current);
    };
  }, [enabled]);
}

function createActivitySegment(screen: string, userId: string, moduleId?: string) {
  const startedAt = new Date().toISOString();
  return {
    id_acesso: `${userId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    tela: screen,
    id_modulo: moduleId,
    inicio: startedAt
  };
}

async function flushActivity(
  segment: { id_acesso: string; tela: string; id_modulo?: string; inicio: string },
  activeSeconds: number
) {
  try {
    await api.trackActivity({
      ...segment,
      tempo_ativo_segundos: activeSeconds
    });
  } catch {
    // Activity tracking should never interrupt navigation or study flow.
  }
}
